/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   บันทึกตรวจรับวัตถุดิบ (QC) — พิมพ์รายวัน

   รับวันที่มาทาง url (/gr-qc-print-daily/:date, รูปแบบ YYYY-MM-DD)
   แล้วดึงใบรับสินค้าทุกใบที่มีวันที่ใบรับสินค้าตรงกับวันนั้น
   มาพิมพ์ต่อกันในชุดเดียว (แต่ละใบขึ้นหน้ากระดาษใหม่ตาม CSS เดิม)
============================================================ */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Modal, Empty, Spin } from "antd";
import { PiPrinterFill } from "react-icons/pi";
import dayjs from "dayjs";

import GoodsReceiptService from "../../../service/ERP/GoodsReceipt.service";
import GrQcPrintPreview from "./GrQcPrintPreview";
import "./gr-qc.css";

const grService = GoodsReceiptService();

/* เงื่อนไขเดียวกับฟอร์มเดี่ยว : IG7 = สารเคมี , PK/PC = บรรจุภัณฑ์ */
const isChemical = (d) =>
  String(d?.stcode || "").toUpperCase().startsWith("IG7");
const isPacking = (d) => {
  const p = String(d?.stcode || "").toUpperCase().slice(0, 2);
  return p === "PK" || p === "PC";
};

export default function GrQcDailyPrintPreview() {
  const { date } = useParams();

  const [list, setList] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const handlePrint = () => window.print();

  useEffect(() => {
    grService
      .qcByDate(date)
      .then(async (res) => {
        const rows = res.data?.data || [];
        setList(rows);
        /* ดึงรายละเอียดทุกใบ เพื่อรวมฟอร์ม RM และ IG ให้อยู่หน้าเดียวกัน */
        const loaded = await Promise.all(
          rows.map((g) =>
            grService
              .qcGet(g.grcode)
              .then((r) => ({ grcode: g.grcode, ...(r.data?.data || {}) }))
              .catch(() => null)
          )
        );
        setDocs(loaded.filter(Boolean));
      })
      .catch((err) => {
        Modal.error({
          title: "Error",
          content: err.response?.data?.message || "โหลดข้อมูลไม่สำเร็จ",
          onOk: () => window.close(),
        });
      })
      .finally(() => setLoading(false));
  }, [date]);

  /* รวมแถวของทุกใบในวันเดียวกัน */
  /* ติดชื่อผู้จำหน่ายของใบนั้น ๆ ไปกับแต่ละแถว ก่อนรวมข้ามใบ
     ไม่งั้นฟอร์มรวมจะโชว์ supplier ของใบแรกให้ทุกแถว */
  const allRows = docs.flatMap((d) =>
    (d.detail || []).map((x) => ({
      ...x,
      supplier: x.supplier || d.gr?.supname || "",
    }))
  );
  const mergedRM = allRows.filter((d) => d.report_type !== "IG");
  const mergedIG = allRows.filter(
    (d) => d.report_type === "IG" && !isChemical(d) && !isPacking(d)
  );
  /* หัวฟอร์มของชุดรวม — ใช้วันที่ที่สั่งพิมพ์เป็นหลัก */
  const mergedHeader = {
    /* ชุดรวมมาจากหลายผู้จำหน่าย หัวฟอร์มจึงไม่ระบุชื่อ ใช้รายแถวแทน */
    gr: { ...(docs[0]?.gr || {}), supname: "", grdate: date },
    qc: { ...(docs[0]?.qc || {}), inspect_date: date },
  };

  return (
    <div className="page-show" id="gr-qc">
      <div className="doc-wrapper">
        <div className="print-bar no-print">
          <Button
            className="bn-center bg-blue-400"
            icon={<PiPrinterFill style={{ fontSize: "1.1rem" }} />}
            onClick={handlePrint}
            disabled={loading || list.length === 0}
          >
            PRINT {list.length > 0 ? `(${list.length} ใบ)` : ""}
          </Button>
        </div>

        {loading && (
          <div className="no-print" style={{ padding: 40, textAlign: "center" }}>
            <Spin />
          </div>
        )}

        {!loading && list.length === 0 && (
          <div className="no-print" style={{ padding: 40, background: "#fff" }}>
            <Empty
              description={`ไม่พบใบรับสินค้าของวันที่ ${dayjs(date).format(
                "DD/MM/YYYY"
              )}`}
            />
          </div>
        )}

        {/* RM และ IG รวมทุกใบของวันนั้นไว้ในฟอร์มเดียว
            ส่วนสารเคมี (IG7) และบรรจุภัณฑ์ (PK/PC) ยังแยกตามใบรับสินค้าเหมือนเดิม */}
        {mergedRM.length > 0 && (
          <GrQcPrintPreview
            embedded
            sections={["RM"]}
            data={{ ...mergedHeader, rowsRM: mergedRM }}
          />
        )}
        {mergedIG.length > 0 && (
          <GrQcPrintPreview
            embedded
            sections={["IG"]}
            data={{ ...mergedHeader, rowsIG: mergedIG }}
          />
        )}
        {docs.map((d) => (
          <GrQcPrintPreview
            key={d.grcode}
            embedded
            sections={["CM", "PK"]}
            data={{
              gr: d.gr || {},
              qc: d.qc || {},
              rowsCM: (d.detail || []).filter(
                (x) => x.report_type === "IG" && isChemical(x)
              ),
              rowsPK: (d.detail || []).filter(
                (x) => x.report_type === "IG" && isPacking(x)
              ),
            }}
          />
        ))}
      </div>
    </div>
  );
}
